import React, { Fragment } from 'react';
import { AppBar, Dropdown, Icon } from '@brighterionai/br-ui';
import { useLocation, useHistory } from 'react-router-dom';
import { RoleService } from 'Services';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import type { AppUrl } from '@brighterion/types/config';
import { useAppStore } from 'Store';
import Alert from '../../common-components/Alert';
import ImpersonationHeader from '../ImpersonationHeader';
import MenuTab, { TABS } from './MenuTab';
import SubNav from './SubNav';

import styles from './Navigation.css';

function AppSwitcher({ appUrls }: { appUrls: AppUrl[] }) {
    const { t } = useTranslation();
    const filteredAppUrls = appUrls?.filter(() => {
        return true;
    });

    if (filteredAppUrls?.length === 0) return null;

    return (
        <AppBar.IconDropdown>
            {filteredAppUrls?.map(({ text, url }, index) => {
                return (
                    <React.Fragment key={url}>
                        <a key={url} className={styles.appLink} target='_blank' href={url} rel='noopener noreferrer'>
                            {t(text)}
                        </a>
                        {index !== filteredAppUrls.length - 1 && <hr tabIndex={-1} className={styles.divider} />}
                    </React.Fragment>
                );
            })}
        </AppBar.IconDropdown>
    );
}

type NavigationProps = {
    options: {
        isMastercardConnectUser: boolean;
    };
};

const Navigation: React.FC<NavigationProps> = ({ options }) => {
    const { isMastercardConnectUser } = options;
    const logout = useAppStore.use.logout?.();
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const appConfig = useAppStore?.use.appConfig?.();
    const authConfig = useAppStore?.use.authConfig?.();
    const appUrls = appConfig?.appUrls ?? [];

    const user = useAppStore.use.user();
    const { username, landlordId, tenantKey } = user;

    // Check if user is in impersonation mode
    const isImpersonationMode = landlordId && landlordId !== tenantKey;

    const { hasCMRoles, hasRMRoles, hasTMRoles } = RoleService;

    const NavTabs = [...TABS(RoleService)];
    const activeRoute = NavTabs.find(({ route }) => location.pathname.includes(route));
    const showActiveRouteSubNav = activeRoute?.showFeature;
    const alertData = useAppStore.use.alertData?.();
    const isAlertShow = useAppStore.use.isAlertShow?.();
    const hideAlert = useAppStore.use.hideAlert?.();

    // As per the role add urls in appSwitcher
    const appSwitcherUrls = [] as AppUrl[];
    if (hasRMRoles) {
        appSwitcherUrls.push(appUrls[0]);
    }
    if (hasCMRoles) {
        appSwitcherUrls.push(appUrls[1]);
    }
    if (hasTMRoles) {
        appSwitcherUrls.push(appUrls[2]);
    }

    return (
        <AppBar isMCConnectUser={false} mcConnectEnv={authConfig?.env || 'staging'} className={styles.appBar}>
            {isImpersonationMode && <ImpersonationHeader />}
            <AppBar.Container>
                {!isMastercardConnectUser && (
                    <AppBar.LogoContainer
                        onContainerClick={() => {
                            history.push('/');
                        }}
                    >
                        <img
                            height='60%'
                            style={{ margin: '0 -0.625rem' }}
                            alt='brighterion mastercard logo'
                            src='https://d3px3a2ky3dqg2.cloudfront.net/images/mastercard_logo.svg'
                        />
                    </AppBar.LogoContainer>
                )}

                <AppBar.LeftContainer>
                    <>
                        {NavTabs.map(({ route, displayName, showFeature }) => {
                            if (!showFeature) return null;
                            return (
                                <MenuTab
                                    key={route}
                                    route={route}
                                    displayName={t(displayName)}
                                    isActive={activeRoute?.route === route}
                                    onClick={() => history.push(route)}
                                />
                            );
                        })}
                        <>
                            <Alert {...alertData} isOpen={isAlertShow} hideAlertHandle={hideAlert} />
                        </>
                    </>
                </AppBar.LeftContainer>
                <AppBar.RightContainer>
                    <div className={styles.appNameContainer}>
                        <span className={clsx(styles.appNameContent, appSwitcherUrls?.length && styles.borderRight)}>
                            Watchlist Screening
                        </span>
                    </div>
                    <div className={styles.appSwitcher}>
                        <AppSwitcher appUrls={appSwitcherUrls} />

                        {
                            // TODO: Re-enable ImportExportQueueButton when WLS bulk upload background process feature is available
                            // /* {hasWLSListAdminAccess && <ImportExportQueueButton />} */
                        }
                        {!isMastercardConnectUser ? (
                            <Dropdown
                                id='avatar'
                                showArrows={false}
                                menuAlignment='right'
                                containerClassName={styles.avatarContainer}
                                menuDropdownClassName={styles.menuDropdown}
                            >
                                <Icon className={styles.avatar} name='avatar' />
                                <div tabIndex={-1} className={styles.displayOnly}>{`Logged in as ${username}`}</div>
                                <button
                                    type='button'
                                    className={styles.buttonGhost}
                                    onClick={handleOnClickEvent(logout)}
                                    onKeyDown={handleKeyEvent(logout)}
                                >
                                    Log out
                                </button>
                            </Dropdown>
                        ) : null}
                    </div>
                </AppBar.RightContainer>
            </AppBar.Container>
            {showActiveRouteSubNav && <SubNav />}
        </AppBar>
    );
};

export default Navigation;
